import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CompletePaymentSepaDto } from 'app/shared/models/dtos/complete-payment/complete-payment-sepa.dto';
import { ContactComponent } from 'app/shared/components/contact/contact.component';
import { BannerComponent } from 'app/shared/components/banner/banner.component';
import { Themes } from 'themes';
import { BackendErrorType, isHandledByErrorInterceptor } from 'app/shared/services/errors/backend.errors';
import { FeeInfoBoxComponent } from './fee-info-box/fee-info-box.component';
import { SepaInputBoxComponent } from './sepa-input-box/sepa-input-box.component';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { Contract } from 'app/shared/models/contract.model';
import { BackendService } from 'app/shared/services/backend.service';
import { ibanValidator } from 'app/shared/components/iban-input/iban-input.validator';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { isPrivateCustomer } from 'app/shared/models/customer.model';

@Component({
  selector: 'ez-sepa-detail',
  imports: [MainLayoutComponent, FeeInfoBoxComponent, SepaInputBoxComponent, ContactComponent, BannerComponent],
  templateUrl: './sepa-detail.component.html',
  styleUrl: './sepa-detail.component.scss',
})
export class SepaDetailComponent {
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly backendService = inject(BackendService);

  readonly landingpageConfig = inject(LandingpageConfigurationStateService).landingpageConfiguration;

  readonly earliestDueDate = computed(() => {
    const landingpageConfig = this.landingpageConfig();
    return landingpageConfig ? this.getEarliestDueDate(landingpageConfig.contracts) : undefined;
  });

  readonly form: FormGroup = this.formBuilder.group({
    iban: ['', [Validators.required, ibanValidator]],
    givesSepaMandate: [false, Validators.requiredTrue],
  });

  onBackToOverview() {
    this.router.navigate(['case']);
  }

  onSubmit() {
    const givesSepaMandate = this.form.controls['givesSepaMandate'].value;
    if (givesSepaMandate) {
      const iban: string = this.form.controls['iban'].value;
      const payload: CompletePaymentSepaDto = {
        iban: iban,
        reference: this.landingpageConfig()!.reference,
      };
      this.backendService.completePaymentSepa(payload).subscribe({
        next: () => this.router.navigate(['completed']),
        error: (error) => {
          if (error.message === BackendErrorType.ALREADY_SENT) {
            this.router.navigate(['already-sent']);
          } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
            this.router.navigate(['verification-error']);
          } else if (!isHandledByErrorInterceptor(error)) {
            this.form.setErrors({ [error.message]: true });
          }
        },
      });
    }
  }

  private getEarliestDueDate(contracts: Contract[]): Date {
    return contracts
      .flatMap((contract) => {
        return contract.items.map((item) => {
          return item.dueDate;
        });
      })
      .reduce((earlyDueDate, dueDate) => {
        return earlyDueDate < dueDate ? earlyDueDate : dueDate;
      });
  }

  protected readonly Themes = Themes;
  protected readonly isPrivateCustomer = isPrivateCustomer;
}
